/**
 * Histórico de métricas — heatmap anual (12 meses × N métricas) filtrado
 * por nivel y proyecto. Trae los valores de una sola query y agrupa en el
 * cliente.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  type KpiDefinition,
  type KpiValue,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { EvaluationsTabs } from "@/features/evaluations/EvaluationsTabs";
import { api } from "@/lib/api";
import { canSeeDef } from "@/lib/eval-visibility";
import { MetricHeatmap } from "@/features/evaluations/MetricHeatmap";

export const Route = createFileRoute("/evaluations/history")({
  component: HistoryPage,
});

function HistoryPage() {
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const [level, setLevel] = useState<"all" | (typeof EVAL_LEVELS)[number]>("all");

  // 12 meses retrocediendo desde el actual.
  const periods = useMemo(() => buildLast12Months(), []);
  const from = periods[0]!;
  const to = periods[periods.length - 1]!;

  const defsQ = useQuery<{ definitions: KpiDefinition[] }>({
    queryKey: ["metric-definitions"],
    queryFn: () => api.get("/kpi-definitions"),
  });

  const valuesQ = useQuery<{
    values: Array<{ value: KpiValue; definition: KpiDefinition }>;
  }>({
    queryKey: ["metric-values", "history", from, to],
    queryFn: () => api.get(`/kpi-values?periodFrom=${from}&periodTo=${to}&projectId=`),
  });

  const valueByDefAndPeriod = useMemo(() => {
    const m = new Map<string, Map<string, number | null>>();
    for (const row of valuesQ.data?.values ?? []) {
      const inner = m.get(row.value.definitionId) ?? new Map();
      inner.set(row.value.period, row.value.value);
      m.set(row.value.definitionId, inner);
    }
    return m;
  }, [valuesQ.data]);

  const visibleDefs = useMemo(() => {
    if (!me.data || !defsQ.data) return [];
    return defsQ.data.definitions
      .filter((d) => d.level !== "project") // proyectos viven en su detalle
      .filter((d) => canSeeDef(d.visibility, me.data.role))
      .filter((d) => level === "all" || d.level === level);
  }, [me.data, defsQ.data, level]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="evaluaciones"
        title="Histórico de métricas"
        subtitle="Heatmap de los últimos 12 meses. Verde = bien, amarillo = atención, rojo = riesgo, gris = sin dato."
        actions={
          <Link to="/evaluations" className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        }
      />

      <EvaluationsTabs current="history" role={me.data?.role ?? "member"} />

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2">
        <FilterPill label="Todos" active={level === "all"} onClick={() => setLevel("all")} />
        {EVAL_LEVELS.filter((l) => l !== "project").map((l) => (
          <FilterPill
            key={l}
            label={EVAL_LEVEL_LABELS[l]}
            active={level === l}
            onClick={() => setLevel(l)}
          />
        ))}
      </div>

      {(defsQ.isLoading || valuesQ.isLoading) && (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner loading-md text-primary" />
        </div>
      )}

      {!defsQ.isLoading && (
        <MetricHeatmap
          definitions={visibleDefs}
          periods={periods}
          valueByDefAndPeriod={valueByDefAndPeriod}
        />
      )}
    </div>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-sm ${active ? "btn-primary" : "btn-ghost"}`}
    >
      {label}
    </button>
  );
}

function buildLast12Months(): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getUTCFullYear(), now.getUTCMonth() - i, 1);
    months.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return months;
}
